import { environment } from "./environment";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    gtagInitialized?: boolean;
  }
}

const ANALYTICS_COOKIE = "catvanteAnalyticsCookiesAccepted=true";

const hasMeasurementId = () => {
  const id = environment.google.analyticsMeasurementId;
  return typeof id === "string" && id.trim().length > 0;
};

const hasAdsId = () => {
  const id = environment.google.adsConversionId;
  return typeof id === "string" && id.trim().length > 0;
};

const getAdsConversionLabel = (eventName?: string) => {
  if (!eventName) return undefined;
  const labels = environment.google.adsConversionLabels ?? {};
  switch (eventName) {
    case "purchase":
      return labels.purchase;
    case "begin_checkout":
      return labels.beginCheckout;
    case "add_to_cart":
      return labels.addToCart;
    case "add_payment_info":
      return labels.addPaymentInfo;
    default:
      return undefined;
  }
};

const getSendToTargets = (eventName?: string) => {
  const targets: string[] = [];
  if (hasMeasurementId()) {
    targets.push(environment.google.analyticsMeasurementId!.trim());
  }
  if (hasAdsId()) {
    const adsId = environment.google.adsConversionId!.trim();
    const adsLabel = getAdsConversionLabel(eventName);
    targets.push(adsLabel ? `${adsId}/${adsLabel}` : adsId);
  }
  return targets;
};

export const hasAnalyticsConsent = () =>
  typeof document !== "undefined" &&
  document.cookie.includes(ANALYTICS_COOKIE);

const setupGtagStub = () => {
  if (typeof window === "undefined") {
    return;
  }
  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer!.push(arguments);
    };
};

export const initAnalytics = () => {
  if (
    typeof window === "undefined" ||
    (!hasMeasurementId() && !hasAdsId()) ||
    !hasAnalyticsConsent()
  ) {
    return false;
  }

  setupGtagStub();

  if (window.gtagInitialized) {
    return true;
  }

  const measurementId = environment.google.analyticsMeasurementId;
  const adsId = environment.google.adsConversionId;
  const scriptId = "ga-gtag-script";
  if (!document.getElementById(scriptId)) {
    const script = document.createElement("script");
    script.async = true;
    const firstId = measurementId ?? adsId;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${firstId}`;
    script.id = scriptId;
    document.head.appendChild(script);
  }

  window.gtag?.("consent", "update", {
    ad_storage: "granted",
    analytics_storage: "granted",
    functionality_storage: "granted",
  });
  window.gtag?.("js", new Date());
  if (measurementId) {
    window.gtag?.("config", measurementId, { send_page_view: false });
  }
  if (adsId) {
    window.gtag?.("config", adsId);
  }
  window.gtagInitialized = true;

  return true;
};

export const trackGtagEvent = (
  name: string,
  params?: Record<string, unknown>
) => {
  if ((!hasMeasurementId() && !hasAdsId()) || !hasAnalyticsConsent()) {
    return;
  }

  const initialized = initAnalytics();
  if (!initialized || typeof window === "undefined" || typeof window.gtag !== "function") {
    return;
  }

  const sendTo = getSendToTargets(name);
  const payload =
    sendTo.length > 0 ? { ...params, send_to: sendTo } : params ?? {};
  window.gtag("event", name, payload);
};

export const trackPageView = (path?: string, title?: string) => {
  if (typeof window === "undefined") {
    return;
  }

  const pagePath = path ?? window.location?.pathname;
  const pageLocation = typeof window.location !== "undefined" ? window.location.href : undefined;

  trackGtagEvent("page_view", {
    page_path: pagePath,
    page_location: pageLocation,
    page_title: title,
  });
};
